import React, { useEffect, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import LGVNotificationBanner from "../../components/lgv/lgv_notification_banner";
import { showStatus as showLGVStatus } from "../../utils/status_helpers";

const EVENT_TYPES = [
  "Car Show", "VW Event", "Pet Show", "Art Show", "BBQ Competition",
  "Chili Cookoff", "Community Festival", "County Fair", "School Fundraiser", "Other",
];

const DASHBOARD_PAGE_SIZE = 25;
const PROSPECT_WORKFLOW_TIMEOUT_MS = 9 * 60 * 1000;
const REFERENCE_CACHE_KEY = "lgv.sales_dashboard.references.v1";
const UI_CACHE_KEY = "lgv.sales_dashboard.ui.v1";
const REFERENCE_CACHE_TTL_MS = 5 * 60 * 1000;

const readSessionValue = (key, fallback = null) => {
  try {
    const value = window.sessionStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeSessionValue = (key, value) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Session storage is an optional read optimization.
  }
};

const readReferenceCache = () => {
  const cached = readSessionValue(REFERENCE_CACHE_KEY);
  if (!cached || Date.now() - Number(cached.savedAt || 0) > REFERENCE_CACHE_TTL_MS) return null;
  return cached;
};

const eventTypeDocumentId = (name) => encodeURIComponent(String(name || "").trim().toLowerCase());

const DEFAULT_PROSPECT = {
  eventName: "",
  eventType: "Car Show",
  organizationName: "",
  contactName: "",
  email: "",
  phone: "",
  website: "",
  facebookUrl: "",
  instagramUrl: "",
  city: "",
  state: "OR",
  eventDate: "",
  fitScore: 5,
  notes: "",
};

const DEFAULT_PANELS = {
  filters: true,
  prospectSearch: true,
  review: false,
  email: false,
  social: false,
  research: false,
  followup: false,
  discovery: false,
  vendors: false,
  usage: false,
};

const isHttpUrl = (value) => {
  try {
    const url = new URL(String(value || "").trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const dateOnly = (date) =>
  [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
const prospectDate = (prospect) => prospect.eventStartDate || prospect.eventDate || "";

const parseProspectDate = (prospect) => {
  const match = String(prospectDate(prospect)).match(/^\d{4}-\d{2}-\d{2}/);
  if (!match) return null;
  const parsed = new Date(`${match[0]}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const displayProspectDate = (prospect) => {
  const parsed = parseProspectDate(prospect);
  return parsed
    ? parsed.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
    : prospectDate(prospect) || "Not listed";
};

const isPastEvent = (prospect) => {
  const parsed = parseProspectDate(prospect);
  if (!parsed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
};

const draftBody = (prospect) =>
  prospect.emailOutreachDraft ||
  prospect.facebookOutreachDraft ||
  prospect.instagramOutreachDraft ||
  prospect.contactFormDraft ||
  prospect.outreachDraft ||
  prospect.suggestedSocialDm ||
  "";

function SalesDashboardMerged() {
  const initialReferences = readReferenceCache();
  const initialUi = readSessionValue(UI_CACHE_KEY, {});
  const [searchPhrases, setSearchPhrases] = useState(initialReferences?.searchPhrases || []);
  const [eventTypes, setEventTypes] = useState(initialReferences?.eventTypes || EVENT_TYPES);
  const [panelData, setPanelData] = useState({});
  const [todayUsage, setTodayUsage] = useState({});
  const [workflowCounts, setWorkflowCounts] = useState({
    review: 0,
    email: 0,
    social: 0,
    research: 0,
    followup: 0,
    discovery: 0,
    vendors: 0,
  });
  const panelPagesRef = useRef({});
  const [panelPagination, setPanelPagination] = useState({});
  const [loadingPanel, setLoadingPanel] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [openPanels, setOpenPanels] = useState(DEFAULT_PANELS);
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_PROSPECT);
  const [searchText, setSearchText] = useState(initialUi.searchText || "");
  const [eventTypeFilter, setEventTypeFilter] = useState(initialUi.eventTypeFilter || "all");
  const [scoreFilter, setScoreFilter] = useState(initialUi.scoreFilter || "all");
  const [draftEdits, setDraftEdits] = useState({});
  const [working, setWorking] = useState({});
  const [searching, setSearching] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [prospectSearch, setProspectSearch] = useState({
    searchPhrase: "",
    eventType: "",
    newEventType: "",
    oneTime: false,
    vendor: false,
  });

  const showStatus = (type, message, duration) =>
    showLGVStatus(setStatus, { type, message, startedAt: Date.now() }, duration);

  const showTimedStatus = (message, progressKey = "") => {
    showLGVStatus(setStatus, {
      type: "info",
      message,
      persistent: true,
      progressKey,
      startedAt: Date.now(),
    }, null);
  };

  useEffect(() => {
    if (!status?.startedAt) {
      setElapsedSeconds(0);
      return undefined;
    }

    const updateElapsed = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - status.startedAt) / 1000)));
    };

    updateElapsed();
    const timer = setInterval(updateElapsed, 1000);
    return () => clearInterval(timer);
  }, [status?.persistent, status?.startedAt]);

  const loadDashboard = async ({ forceReferenceData = false } = {}) => {
    try {
      setLoading(true);
      const cachedReferences = forceReferenceData ? null : readReferenceCache();
      const metricsPromise = Promise.all([
        getCountFromServer(query(collection(db, "prospect_candidates"), where("status", "==", "pending_review"))),
        getCountFromServer(query(
          collection(db, "prospects"),
          where("outreachQueueStatus", "==", "ready_for_review"),
          where("recommendedOutreachChannel", "==", "email")
        )),
        getCountFromServer(query(
          collection(db, "prospects"),
          where("outreachQueueStatus", "==", "ready_for_review"),
          where("recommendedOutreachChannel", "in", ["facebook", "instagram", "contact_form"])
        )),
        getCountFromServer(query(collection(db, "prospects"), where("outreachQueueStatus", "==", "not_ready"))),
        getCountFromServer(query(collection(db, "prospects"), where("outreachQueueStatus", "==", "sent"))),
        getCountFromServer(collection(db, "prospect_search_phrases")),
        getCountFromServer(query(collection(db, "discovery_jobs"), where("phraseId", "==", ""))),
        getCountFromServer(collection(db, "prospect_vendor_urls")),
      ]);
      const [phraseSnapshot, eventTypeSnapshot, usageSnapshot, countSnapshots] = await Promise.all([
        cachedReferences
          ? Promise.resolve(null)
          : getDocs(query(collection(db, "prospect_search_phrases"), orderBy("searchPhrase", "asc"))),
        cachedReferences
          ? Promise.resolve(null)
          : getDocs(query(collection(db, "prospect_event_types"), orderBy("name", "asc"))),
        getDoc(doc(db, "sales_api_usage_daily", dateOnly(new Date()))),
        metricsPromise,
      ]);
      setWorkflowCounts({
        review: countSnapshots[0].data().count,
        email: countSnapshots[1].data().count,
        social: countSnapshots[2].data().count,
        research: countSnapshots[3].data().count,
        followup: countSnapshots[4].data().count,
        discovery: countSnapshots[5].data().count + countSnapshots[6].data().count,
        vendors: countSnapshots[7].data().count,
      });
      const nextSearchPhrases = phraseSnapshot
        ? phraseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
        : cachedReferences?.searchPhrases || [];
      setSearchPhrases(nextSearchPhrases);
      setTodayUsage(usageSnapshot.exists() ? { id: usageSnapshot.id, ...usageSnapshot.data() } : {});
      const storedEventTypes = eventTypeSnapshot
        ? eventTypeSnapshot.docs
            .map((item) => String(item.data().name || "").trim())
            .filter(Boolean)
        : cachedReferences?.eventTypes || EVENT_TYPES;
      if (storedEventTypes.length === 0) {
        await Promise.all(EVENT_TYPES.map((name) => setDoc(
          doc(db, "prospect_event_types", eventTypeDocumentId(name)),
          { name, active: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
          { merge: true }
        )));
        setEventTypes(EVENT_TYPES);
      } else {
        setEventTypes(storedEventTypes);
      }
      writeSessionValue(REFERENCE_CACHE_KEY, {
        savedAt: Date.now(),
        searchPhrases: nextSearchPhrases.map((item) => ({
          id: item.id,
          searchPhrase: item.searchPhrase || "",
          eventType: item.eventType || "",
          active: item.active !== false,
          cadence: item.cadence || "weekly",
          nextSearchPage: item.nextSearchPage || 1,
          initialCrawlCompleted: item.initialCrawlCompleted === true,
          lastResultsFound: item.lastResultsFound || 0,
          totalResultsFound: item.totalResultsFound || 0,
          lastCandidatesCreated: item.lastCandidatesCreated || 0,
          lastProspectsCreated: item.lastProspectsCreated || 0,
          lastDuplicatesSkipped: item.lastDuplicatesSkipped || 0,
          lastSearchStatus: item.lastSearchStatus || "",
          lastSearchError: item.lastSearchError || "",
          lastJobId: item.lastJobId || "",
        })),
        eventTypes: storedEventTypes,
      });
    } catch (error) {
      console.error("Failed to load Prospect Engine:", error);
      showStatus("error", "Failed to load the Prospect Engine.");
    } finally {
      setLoading(false);
    }
  };

  // The loader is intentionally run once when this route mounts.
  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeSessionValue(UI_CACHE_KEY, {
      searchText,
      eventTypeFilter,
      scoreFilter,
    });
  }, [searchText, eventTypeFilter, scoreFilter]);

  const setPanelItems = (panel, items) => {
    setPanelData((current) => ({ ...current, [panel]: items }));
  };

  const panelQuery = (panel, cursor) => {
    const paging = [orderBy(panel === "discovery" ? "startedAt" : panel === "usage" ? "date" : "createdAt", "desc")];
    if (cursor) paging.push(startAfter(cursor));
    paging.push(limit(DASHBOARD_PAGE_SIZE));

    if (panel === "review") {
      return query(collection(db, "prospect_candidates"), where("status", "==", "pending_review"), ...paging);
    }
    if (panel === "email") {
      return query(collection(db, "prospects"), where("outreachQueueStatus", "==", "ready_for_review"), where("recommendedOutreachChannel", "==", "email"), ...paging);
    }
    if (panel === "social") {
      return query(collection(db, "prospects"), where("outreachQueueStatus", "==", "ready_for_review"), where("recommendedOutreachChannel", "in", ["facebook", "instagram", "contact_form"]), ...paging);
    }
    if (panel === "research") {
      return query(collection(db, "prospects"), where("outreachQueueStatus", "==", "not_ready"), ...paging);
    }
    if (panel === "followup") {
      return query(collection(db, "prospects"), where("outreachQueueStatus", "==", "sent"), ...paging);
    }
    if (panel === "discovery") return query(collection(db, "discovery_jobs"), ...paging);
    if (panel === "vendors") return query(collection(db, "prospect_vendor_urls"), ...paging);
    if (panel === "usage") return query(collection(db, "sales_api_usage_daily"), ...paging);
    throw new Error(`Unknown dashboard panel: ${panel}`);
  };

  const loadPanelPage = async (panel, pageIndex = 0, { force = false } = {}) => {
    const cachedPage = panelPagesRef.current[panel]?.[pageIndex];
    if (cachedPage && !force) {
      setPanelItems(panel, cachedPage.items);
      setPanelPagination((current) => ({ ...current, [panel]: { pageIndex, hasMore: cachedPage.hasMore } }));
      return;
    }

    const previousPage = pageIndex > 0 ? panelPagesRef.current[panel]?.[pageIndex - 1] : null;
    if (pageIndex > 0 && !previousPage?.cursor) return;

    try {
      setLoadingPanel(panel);
      setPanelItems(panel, []);
      let items = [];
      let cursor = previousPage?.cursor || null;
      let hasMore = false;

      if (panel === "discovery") {
        const priorPages = panelPagesRef.current.discovery || {};
        const seenSearches = new Set(
          Object.entries(priorPages)
            .filter(([index]) => Number(index) < pageIndex)
            .flatMap(([, page]) => page.items)
            .map((job) => job.phraseId || `${String(job.searchTerm || "").trim().toLowerCase()}|${String(job.eventType || "").trim().toLowerCase()}`)
        );
        let keepScanning = true;

        while (items.length < DASHBOARD_PAGE_SIZE && keepScanning) {
          const snapshot = await getDocs(panelQuery(panel, cursor));
          keepScanning = snapshot.size === DASHBOARD_PAGE_SIZE;
          hasMore = keepScanning;

          for (let index = 0; index < snapshot.docs.length; index += 1) {
            const itemDoc = snapshot.docs[index];
            const item = { id: itemDoc.id, ...itemDoc.data() };
            const searchKey = item.phraseId || `${String(item.searchTerm || "").trim().toLowerCase()}|${String(item.eventType || "").trim().toLowerCase()}`;
            cursor = itemDoc;
            if (seenSearches.has(searchKey)) continue;
            seenSearches.add(searchKey);
            items.push(item);
            if (items.length === DASHBOARD_PAGE_SIZE) {
              hasMore = index < snapshot.docs.length - 1 || snapshot.size === DASHBOARD_PAGE_SIZE;
              break;
            }
          }

          if (snapshot.empty) {
            hasMore = false;
            break;
          }
        }
      } else {
        const snapshot = await getDocs(panelQuery(panel, cursor));
        items = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        cursor = snapshot.docs[snapshot.docs.length - 1] || null;
        hasMore = snapshot.size === DASHBOARD_PAGE_SIZE;
      }
      const page = {
        items,
        cursor,
        hasMore,
      };
      const retainedPages = pageIndex === 0 ? {} : (panelPagesRef.current[panel] || {});
      panelPagesRef.current = {
        ...panelPagesRef.current,
        [panel]: { ...retainedPages, [pageIndex]: page },
      };
      setPanelItems(panel, items);
      setPanelPagination((current) => ({ ...current, [panel]: { pageIndex, hasMore: page.hasMore } }));
    } catch (error) {
      console.error(`Failed to load ${panel} panel:`, error);
      showStatus("error", `Failed to load the ${panel} panel.`);
    } finally {
      setLoadingPanel("");
    }
  };

  const refreshPanel = (panel) => loadPanelPage(panel, 0, { force: true });
  const nextPanelPage = (panel) => loadPanelPage(panel, (panelPagination[panel]?.pageIndex || 0) + 1);
  const previousPanelPage = (panel) => loadPanelPage(panel, Math.max(0, (panelPagination[panel]?.pageIndex || 0) - 1));

  const togglePanel = (panel) =>
    setOpenPanels((current) => ({ ...current, [panel]: !current[panel] }));

  const openOnlyPanel = (panel) =>
    setOpenPanels(
      Object.keys(DEFAULT_PANELS).reduce((result, key) => ({ ...result, [key]: key === panel }), {})
    );

  const handleMetricClick = (panel) => {
    openOnlyPanel(panel);
    loadPanelPage(panel, 0);
  };

  const setBusy = (id, action) => setWorking((current) => ({ ...current, [id]: action }));
  const clearBusy = (id) =>
    setWorking((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });

  const handleAddProspect = async (event) => {
    event.preventDefault();
    if (!formData.eventName.trim()) {
      showStatus("error", "Event name is required.");
      return;
    }

    try {
      await addDoc(collection(db, "prospects"), {
        ...formData,
        eventName: formData.eventName.trim(),
        organizationName: formData.organizationName.trim(),
        contactName: formData.contactName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        website: formData.website.trim(),
        facebookUrl: formData.facebookUrl.trim(),
        instagramUrl: formData.instagramUrl.trim(),
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        eventDate: formData.eventDate || null,
        fitScore: Number(formData.fitScore),
        status: "new",
        source: "manual",
        agentStatus: "manual",
        discoveredBy: "manual",
        researchStatus: "not_started",
        socialResearchStatus: "not_started",
        outreachDraftStatus: "not_started",
        outreachQueueStatus: "not_ready",
        collectionStage: "needs_research",
        queuePriority: Number(formData.fitScore),
        contactHistory: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setFormData(DEFAULT_PROSPECT);
      setShowAddProspect(false);
      setSearchText("");
      setEventTypeFilter("all");
      setScoreFilter("all");
      await loadDashboard();
      openOnlyPanel("research");
      await refreshPanel("research");
      showStatus("success", "Prospect added to the Research Queue.");
    } catch (error) {
      console.error("Failed to add prospect:", error);
      showStatus("error", "Failed to add the prospect.");
    }
  };

  const handleProspectSearchChange = (field, value) => {
    setProspectSearch((current) => ({ ...current, [field]: value }));

    if (field === "searchPhrase") {
      const savedPhrase = searchPhrases.find(
        (item) => item.searchPhrase?.trim().toLowerCase() === value.trim().toLowerCase()
      );
      if (savedPhrase?.eventType) {
        setProspectSearch((current) => ({
          ...current,
          searchPhrase: value,
          eventType: savedPhrase.eventType,
          newEventType: "",
        }));
      }
    }
  };

  const saveEventType = async (name) => {
    const cleanName = String(name || "").trim();
    if (!cleanName) return;
    await setDoc(
      doc(db, "prospect_event_types", eventTypeDocumentId(cleanName)),
      { name: cleanName, active: true, updatedAt: serverTimestamp() },
      { merge: true }
    );
    setEventTypes((current) => Array.from(new Set([...current, cleanName])).sort());
  };

  const handleProspectSearch = async (event) => {
    event.preventDefault();
    const searchPhrase = prospectSearch.searchPhrase.trim();
    const newEventType = prospectSearch.newEventType.trim();
    const eventType = newEventType || prospectSearch.eventType.trim();
    const oneTime = prospectSearch.oneTime === true;
    const vendor = prospectSearch.vendor === true;

    if (!searchPhrase || !eventType) {
      showStatus("error", "Search Phrase and Event Type are required.");
      return;
    }
    if (vendor && !isHttpUrl(searchPhrase)) {
      showStatus("error", "A Vendor entry must use a valid http:// or https:// URL.");
      return;
    }

    let phraseId = "";

    try {
      setSearching(true);
      if (newEventType) {
        await saveEventType(newEventType);
      }

      if (vendor) {
        const existingVendor = await getDocs(query(
          collection(db, "prospect_vendor_urls"),
          where("url", "==", searchPhrase),
          limit(1)
        ));
        if (existingVendor.empty) {
          await addDoc(collection(db, "prospect_vendor_urls"), {
            url: searchPhrase,
            eventType,
            status: "to_contact",
            source: "prospect_search",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } else {
          await updateDoc(existingVendor.docs[0].ref, {
            eventType,
            status: "to_contact",
            updatedAt: serverTimestamp(),
          });
        }
        await loadDashboard({ forceReferenceData: Boolean(newEventType) });
        setProspectSearch({
          searchPhrase: "",
          eventType: "",
          newEventType: "",
          oneTime: false,
          vendor: false,
        });
        openOnlyPanel("vendors");
        await refreshPanel("vendors");
        showStatus("success", existingVendor.empty
          ? "Vendor URL saved for future contact."
          : "Vendor URL was already tracked and has been updated.");
        return;
      }

      if (!oneTime) {
        const existing = searchPhrases.find(
          (item) => item.searchPhrase?.trim().toLowerCase() === searchPhrase.toLowerCase()
        );

        if (existing) {
          phraseId = existing.id;
          await updateDoc(doc(db, "prospect_search_phrases", phraseId), {
            searchPhrase,
            eventType,
            active: true,
            updatedAt: serverTimestamp(),
          });
        } else {
          const phraseRef = await addDoc(collection(db, "prospect_search_phrases"), {
            searchPhrase,
            normalizedPhrase: searchPhrase.toLowerCase(),
            eventType,
            active: true,
            cadence: "weekly",
            nextSearchPage: 1,
            initialCrawlCompleted: false,
            lastSearchStatus: "pending",
            lastResultsFound: 0,
            totalResultsFound: 0,
            lastCandidatesCreated: 0,
            lastDuplicatesSkipped: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          phraseId = phraseRef.id;
        }
      }

      showStatus("info", "Prospect Search is running. This may take a moment.");
      const discoverProspectCandidates = httpsCallable(getFunctions(), "discoverProspectCandidates");
      const result = await discoverProspectCandidates({ searchTerm: searchPhrase, eventType, phraseId });
      await loadDashboard({ forceReferenceData: true });
      setProspectSearch({
        searchPhrase: "",
        eventType: "",
        newEventType: "",
        oneTime: false,
        vendor: false,
      });
      openOnlyPanel("review");
      await refreshPanel("review");
      showStatus(
        "success",
        `${result.data?.localTestMode ? "Local test: " : ""}${oneTime ? "One-time search" : "Prospect Search"} moved ${result.data?.autoApprovedCount || 0} likely event(s) to Research, saved ${result.data?.createdCount || 0} result(s) for review, and skipped ${result.data?.duplicatesSkipped || 0} already tracked result(s).`
      );
    } catch (error) {
      console.error("Prospect Search failed:", error);
      if (phraseId) {
        await updateDoc(doc(db, "prospect_search_phrases", phraseId), {
          lastSearchStatus: "failed",
          lastSearchError: error?.message || "Prospect Search failed.",
          lastSearchedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }
      showStatus("error", error?.message || "Prospect Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const handleRerunDiscovery = async (search) => {
    const job = search.latestJob || {};
    const searchTerm = String(search.searchPhrase || job.searchTerm || "").trim();
    const eventType = String(search.eventType || job.eventType || "").trim();
    const phraseId = search.oneTime ? "" : search.id;
    const workKey = `discovery-rerun-${search.id}`;

    if (!searchTerm || !eventType) {
      showStatus("error", "This search is missing its Search Phrase or Event Type.");
      return;
    }

    try {
      setWorking((current) => ({ ...current, [workKey]: true }));
      showStatus("info", `Rerunning prospect search: ${searchTerm}`);
      const discoverProspectCandidates = httpsCallable(getFunctions(), "discoverProspectCandidates");
      const result = await discoverProspectCandidates({ searchTerm, eventType, phraseId });
      await loadDashboard({ forceReferenceData: true });
      openOnlyPanel("discovery");
      await refreshPanel("discovery");
      showStatus(
        "success",
        `Prospect Search moved ${result.data?.autoApprovedCount || 0} likely event(s) to Research, saved ${result.data?.createdCount || 0} result(s) for review, and skipped ${result.data?.duplicatesSkipped || 0} already tracked result(s).`
      );
    } catch (error) {
      console.error("Prospect Search rerun failed:", error);
      showStatus("error", error?.message || "Prospect Search rerun failed.");
    } finally {
      setWorking((current) => ({ ...current, [workKey]: false }));
    }
  };

  const handleDeleteDiscovery = async (search) => {
    const job = search.latestJob || {};
    const label = String(search.searchPhrase || job.searchTerm || "this search").trim();
    const confirmation = search.oneTime
      ? `Delete the one-time discovery run for "${label}"?`
      : `Delete the saved search "${label}"? It will no longer run weekly.`;

    if (!window.confirm(confirmation)) return;

    const workKey = `discovery-delete-${search.id}`;
    try {
      setWorking((current) => ({ ...current, [workKey]: true }));
      if (search.oneTime) {
        if (!job.id) throw new Error("The discovery run could not be identified.");
        await deleteDoc(doc(db, "discovery_jobs", job.id));
      } else {
        await deleteDoc(doc(db, "prospect_search_phrases", search.id));
      }
      await loadDashboard({ forceReferenceData: true });
      openOnlyPanel("discovery");
      await refreshPanel("discovery");
      showStatus("success", search.oneTime
        ? "The one-time discovery run was deleted."
        : "The saved search was deleted and will no longer run weekly.");
    } catch (error) {
      console.error("Failed to delete discovery search:", error);
      showStatus("error", error?.message || "Failed to delete the discovery search.");
    } finally {
      setWorking((current) => ({ ...current, [workKey]: false }));
    }
  };

  const handleRunProspectWorkflow = async () => {
    const progressRef = doc(collection(db, "agent_runs"));
    let unsubscribeProgress = null;
    let currentBatchNumber = 1;

    try {
      setRunningAutomation("workflow");
      showTimedStatus(
        "Preparing the Research and Draft queue. No outreach will be sent.",
        "workflow-preparing"
      );

      unsubscribeProgress = onSnapshot(progressRef, (snapshot) => {
        if (!snapshot.exists()) return;

        const progress = snapshot.data();
        const current = Number(progress.currentProspectIndex || 0);
        const total = Number(progress.totalProspects || 0);

        if (progress.status !== "running" || current < 1 || total < 1) return;

        const progressKey = `${progressRef.id}:${currentBatchNumber}:${current}`;
        const phase = progress.currentPhase || "research";
        const action = phase === "draft"
          ? "Writing a draft for"
          : phase === "social research"
            ? "Researching socials for"
            : "Researching";

        setStatus((currentStatus) => ({
          type: "info",
          message: `Batch ${currentBatchNumber}: ${current}/${total} ${action} ${progress.currentProspectName || "prospect"}.`,
          persistent: true,
          progressKey,
          startedAt: currentStatus?.progressKey === progressKey
            ? currentStatus.startedAt
            : Date.now(),
        }));
      });

      const runProspectWorkflow = httpsCallable(
        getFunctions(),
        "runProspectWorkflow",
        { timeout: PROSPECT_WORKFLOW_TIMEOUT_MS }
      );
      const excludedProspectIds = [];
      const totals = { queued: 0, completed: 0, failed: 0 };
      let localTestMode = false;

      while (true) {
        showTimedStatus(
          `Preparing Research and Draft batch ${currentBatchNumber}, up to 15 prospects.`,
          `workflow-batch:${currentBatchNumber}`
        );
        const result = await runProspectWorkflow({
          allowManualWhenDisabled: true,
          progressId: progressRef.id,
          batchLimit: 15,
          excludeProspectIds: excludedProspectIds,
        });

        if (result.data?.skipped) {
          const reason = result.data.reason === "emergencyStop"
            ? "the emergency stop is enabled"
            : "automation is disabled";
          showStatus("warning", `Prospect automation did not run because ${reason}.`);
          return;
        }

        const metrics = result.data?.metrics || {};
        const queued = Number(metrics.queued || 0);
        totals.queued += queued;
        totals.completed += Number(metrics.completed || 0);
        totals.failed += Number(metrics.failed || 0);
        localTestMode = localTestMode || result.data?.localTestMode === true;
        (result.data?.failed || []).forEach((failure) => {
          if (failure.prospectId && !excludedProspectIds.includes(failure.prospectId)) {
            excludedProspectIds.push(failure.prospectId);
          }
        });

        if (Number(result.data?.remaining || 0) < 1 || queued < 1) break;
        currentBatchNumber += 1;
      }

      await loadDashboard();
      const visiblePanel = ["review", "email", "social", "research", "followup", "discovery", "usage"]
        .find((panel) => openPanels[panel]);
      if (visiblePanel) await refreshPanel(visiblePanel);
      showStatus(
        totals.failed > 0 ? "warning" : "success",
        `${localTestMode ? "Local test: " : ""}Prospect automation complete across ${currentBatchNumber} batch(es). Queued: ${totals.queued}. Completed: ${totals.completed}. Failed: ${totals.failed}.`
      );
    } catch (error) {
      console.error("Prospect automation failed:", error);
      showStatus("error", error?.message || "Prospect automation failed.");
    } finally {
      if (unsubscribeProgress) unsubscribeProgress();
      setRunningAutomation("");
    }
  };

  const runResearchAndDraft = async (prospectId) => {
    const runProspectWorkflow = httpsCallable(
      getFunctions(),
      "runProspectWorkflow",
      { timeout: PROSPECT_WORKFLOW_TIMEOUT_MS }
    );
    const result = await runProspectWorkflow({
      allowManualWhenDisabled: true,
      batchLimit: 1,
      prospectIds: [prospectId],
    });
    if (result.data?.skipped) throw new Error("Prospect automation is currently disabled.");
    if (Number(result.data?.metrics?.failed || 0) > 0) {
      throw new Error(result.data?.failed?.[0]?.message || "Research or draft generation failed.");
    }
    return result;
  };

  const getCandidateEventType = (candidate) => candidate.searchEventType ||
    searchPhrases.find((phrase) => phrase.id === candidate.searchPhraseId)?.eventType ||
    (panelData.discovery || []).find((job) => job.id === candidate.discoveryJobId)?.eventType ||
    candidate.eventType ||
    "";

  const handleCollectCandidate = async (candidate) => {
    const sourceUrl = String(candidate.sourceUrl || "").trim();
    const eventType = getCandidateEventType(candidate);
    if (!sourceUrl) {
      showStatus("error", "This discovery result does not have a source URL to collect.");
      return;
    }
    if (!eventType) {
      showStatus("error", "This discovery result does not have an Event Type.");
      return;
    }

    try {
      setBusy(candidate.id, "collection");
      showTimedStatus(
        `Collecting events from ${sourceUrl}.`,
        `candidate:${candidate.id}:collection`
      );
      const existingPhrase = searchPhrases.find(
        (phrase) => String(phrase.searchPhrase || "").trim().toLowerCase() === sourceUrl.toLowerCase()
      );
      let phraseId = existingPhrase?.id || "";
      if (phraseId) {
        await updateDoc(doc(db, "prospect_search_phrases", phraseId), {
          searchPhrase: sourceUrl,
          normalizedPhrase: sourceUrl.toLowerCase(),
          eventType,
          active: true,
          cadence: "weekly",
          updatedAt: serverTimestamp(),
        });
      } else {
        const phraseRef = await addDoc(collection(db, "prospect_search_phrases"), {
          searchPhrase: sourceUrl,
          normalizedPhrase: sourceUrl.toLowerCase(),
          eventType,
          active: true,
          cadence: "weekly",
          nextSearchPage: 1,
          initialCrawlCompleted: false,
          lastSearchStatus: "pending",
          lastResultsFound: 0,
          totalResultsFound: 0,
          lastCandidatesCreated: 0,
          lastDuplicatesSkipped: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        phraseId = phraseRef.id;
      }

      const result = await httpsCallable(getFunctions(), "discoverProspectCandidates")({
        searchTerm: sourceUrl,
        eventType,
        phraseId,
      });
      await httpsCallable(getFunctions(), "reviewProspectCandidate")({
        candidateId: candidate.id,
        action: "archive",
      });
      await loadDashboard({ forceReferenceData: true });
      await refreshPanel("review");
      await refreshPanel("discovery");
      openOnlyPanel("discovery");
      showStatus(
        "success",
        `${result.data?.localTestMode ? "Local test: " : ""}Collection saved as a weekly search. The original review record was archived. Moved ${result.data?.autoApprovedCount || 0} likely event(s) to Research, saved ${result.data?.createdCount || 0} result(s) for review, and skipped ${result.data?.duplicatesSkipped || 0} duplicate(s).`
      );
    } catch (error) {
      console.error("Candidate collection failed:", error);
      showStatus("error", error?.message || "The source URL could not be collected.");
    } finally {
      clearBusy(candidate.id);
    }
  };

  const handleReviewCandidate = async (candidate, action) => {
    let approvedProspectId = "";
    try {
      setBusy(candidate.id, action);
      showTimedStatus(
        action === "approve"
          ? `Researching and drafting outreach for ${candidate.eventName}.`
          : `Archiving ${candidate.eventName} for duplicate prevention.`,
        `candidate:${candidate.id}:${action}`
      );
      const reviewResult = await httpsCallable(getFunctions(), "reviewProspectCandidate")({
        candidateId: candidate.id,
        action,
      });
      let researchResult = null;
      if (action === "approve") {
        approvedProspectId = reviewResult.data?.prospectId || "";
        researchResult = await runResearchAndDraft(approvedProspectId);
      }
      await loadDashboard();
      openOnlyPanel("review");
      await refreshPanel("review");
      showStatus(
        "success",
        action === "approve"
          ? `${researchResult?.data?.localTestMode ? "Local test: " : ""}${candidate.eventName} was researched and moved to an outreach queue with a draft.`
          : `${candidate.eventName} was archived and will remain tracked for duplicate prevention.`
      );
    } catch (error) {
      console.error("Candidate review failed:", error);
      await loadDashboard();
      await refreshPanel("review");
      showStatus(
        "error",
        approvedProspectId
          ? `${candidate.eventName} moved to the Research Queue, but its research or draft failed. It remains there for retry.`
          : error?.message || "Candidate review failed."
      );
    } finally {
      clearBusy(candidate.id);
    }
  };

  const handleRunSavedSearches = async () => {
    try {
      setRunningAutomation("discovery");
      const runWeeklyProspectDiscovery = httpsCallable(getFunctions(), "runWeeklyProspectDiscovery");
      const processedPhraseIds = [];
      const totals = { searched: 0, candidatesCreated: 0, prospectsCreated: 0, failed: 0 };
      let localTestMode = false;
      let batchNumber = 1;

      while (true) {
        showTimedStatus(
          `Running saved-search batch ${batchNumber}, up to 15 search phrases.`,
          `discovery-batch:${batchNumber}`
        );
        const result = await runWeeklyProspectDiscovery({ excludePhraseIds: processedPhraseIds });

        if (result.data?.skipped) {
          showStatus("warning", "Saved searches did not run because the emergency stop is enabled.");
          return;
        }

        const completed = result.data?.completed || [];
        totals.searched += Number(result.data?.searched || 0);
        totals.failed += result.data?.failed?.length || 0;
        totals.candidatesCreated += completed.reduce(
          (total, run) => total + Number(run.candidatesCreated || 0),
          0
        );
        totals.prospectsCreated += completed.reduce(
          (total, run) => total + Number(run.prospectsCreated || 0),
          0
        );
        localTestMode = localTestMode || completed.some((run) => run.localTestMode);
        (result.data?.processedPhraseIds || []).forEach((phraseId) => {
          if (!processedPhraseIds.includes(phraseId)) processedPhraseIds.push(phraseId);
        });

        const remaining = Number(result.data?.remaining || 0);
        if (remaining < 1 || Number(result.data?.searched || 0) < 1) break;
        batchNumber += 1;
      }

      await loadDashboard({ forceReferenceData: true });
      openOnlyPanel("review");
      await refreshPanel("review");
      showStatus(
        totals.failed > 0 ? "warning" : "success",
        `${localTestMode ? "Local test: " : ""}Saved searches complete across ${batchNumber} batch(es). Phrases searched: ${totals.searched}. Moved to Research: ${totals.prospectsCreated}. Saved for review: ${totals.candidatesCreated}. Failed: ${totals.failed}.`
      );
    } catch (error) {
      console.error("Saved Prospect Searches failed:", error);
      showStatus("error", error?.message || "Saved Prospect Searches failed.");
    } finally {
      setRunningAutomation("");
    }
  };

  const handleResearch = async (prospect) => {
    try {
      setBusy(prospect.id, "research");
      showTimedStatus(
        `Researching ${prospect.eventName}, its socials, and its draft.`,
        `prospect:${prospect.id}`
      );
      const researchResult = await runResearchAndDraft(prospect.id);
      await loadDashboard();
      await refreshPanel("research");
      showStatus(
        "success",
        `${researchResult.data?.localTestMode ? "Local test: " : ""}${prospect.eventName} moved to an outreach queue.`
      );
    } catch (error) {
      console.error("Prospect research workflow failed:", error);
      await loadDashboard();
      await refreshPanel("research");
      showStatus("error", error?.message || "Research workflow failed. The prospect remains queued.");
    } finally {
      clearBusy(prospect.id);
    }
  };

  const getDraft = (prospect) =>
    draftEdits[prospect.id] || { subject: prospect.outreachSubject || "", body: draftBody(prospect) };

  const changeDraft = (prospect, field, value) =>
    setDraftEdits((current) => ({
      ...current,
      [prospect.id]: { ...getDraft(prospect), [field]: value },
    }));

  const handleCopyDraft = async (prospect) => {
    const message = getDraft(prospect).body.trim();
    if (!message) {
      showStatus("error", "There is no draft message to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(message);
      showStatus("success", `The draft for ${prospect.eventName} was copied to the clipboard.`);
    } catch (error) {
      console.error("Failed to copy social outreach draft:", error);
      showStatus("error", "The draft could not be copied to the clipboard.");
    }
  };

  const handleRecreateDraft = async (prospect) => {
    try {
      setBusy(prospect.id, "draft");
      showStatus("info", `Recreating the outreach draft for ${prospect.eventName}.`);
      const result = await httpsCallable(
        getFunctions(),
        "generateResearchOutreachDraft"
      )({ prospectId: prospect.id });
      setDraftEdits((current) => {
        const next = { ...current };
        delete next[prospect.id];
        return next;
      });
      await loadDashboard();
      await refreshPanel(openPanels.email ? "email" : "social");
      showStatus(
        "success",
        `${result.data?.localTestMode ? "Local test: " : ""}The draft for ${prospect.eventName} was recreated.`
      );
    } catch (error) {
      console.error("Draft recreation failed:", error);
      showStatus("error", error?.message || "The draft could not be recreated.");
    } finally {
      clearBusy(prospect.id);
    }
  };

  const handleSendEmail = async (prospect) => {
    const draft = getDraft(prospect);
    if (!draft.subject.trim() || !draft.body.trim()) {
      showStatus("error", "A subject and message are required before sending.");
      return;
    }

    try {
      setBusy(prospect.id, "send");
      await httpsCallable(getFunctions(), "sendProspectOutreachEmail")({
        prospectId: prospect.id,
        subject: draft.subject,
        body: draft.body,
      });
      await loadDashboard();
      await refreshPanel("email");
      showStatus("success", `${prospect.eventName} was emailed and moved to Follow Up.`);
    } catch (error) {
      console.error("Prospect email failed:", error);
      showStatus("error", error?.message || "The email could not be sent.");
    } finally {
      clearBusy(prospect.id);
    }
  };

  const handleSocialSent = async (prospect) => {
    const draft = getDraft(prospect);
    if (!draft.body.trim()) {
      showStatus("error", "A message is required before marking it sent.");
      return;
    }

    const today = new Date();
    const followUp = new Date();
    followUp.setDate(today.getDate() + 7);
    const channel = prospect.facebookUrl ? "facebook" : prospect.instagramUrl ? "instagram" : "social";

    try {
      setBusy(prospect.id, "social");
      await updateDoc(doc(db, "prospects", prospect.id), {
        outreachSubject: draft.subject.trim(),
        outreachDraft: draft.body.trim(),
        [`${channel}OutreachDraft`]: draft.body.trim(),
        status: "contacted",
        outreachStatus: "sent",
        outreachQueueStatus: "sent",
        collectionStage: "follow_up",
        lastOutreachChannel: channel,
        lastOutreachDate: dateOnly(today),
        outreachSentAt: serverTimestamp(),
        lastContacted: dateOnly(today),
        followUpDate: dateOnly(followUp),
        updatedAt: serverTimestamp(),
      });
      await loadDashboard();
      await refreshPanel("social");
      showStatus("success", `${prospect.eventName} was moved to Follow Up.`);
    } catch (error) {
      console.error("Failed to mark social outreach sent:", error);
      showStatus("error", "Failed to mark the social message as sent.");
    } finally {
      clearBusy(prospect.id);
    }
  };

  const handleArchiveProspect = async (prospect, panel) => {
    if (!window.confirm(
      `Archive prospect "${prospect.eventName}"? It will be removed from the active workflow queues but retained for history and duplicate prevention.`
    )) return;

    try {
      setBusy(prospect.id, "archive");
      await updateDoc(doc(db, "prospects", prospect.id), {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedFromStatus: prospect.status || "",
        archivedFromOutreachStatus: prospect.outreachStatus || "",
        archivedFromOutreachQueueStatus: prospect.outreachQueueStatus || "",
        archivedFromCollectionStage: prospect.collectionStage || "",
        status: "archived",
        outreachStatus: "archived",
        outreachQueueStatus: "archived",
        collectionStage: "archived",
        updatedAt: serverTimestamp(),
      });
      await loadDashboard();
      await refreshPanel(panel);
      showStatus("success", `${prospect.eventName} was archived.`);
    } catch (error) {
      console.error("Failed to archive prospect:", error);
      showStatus("error", "Failed to archive the prospect.");
    } finally {
      clearBusy(prospect.id);
    }
  };

  const applyFilters = (items = []) => {
    const search = searchText.trim().toLowerCase();
    return items.filter((prospect) => {
      const searchable = [
        prospect.eventName, prospect.organizationName, prospect.contactName, prospect.email,
        prospect.website, prospect.city, prospect.state, prospect.researchSummary,
        prospect.socialResearchSummary, prospect.sourceTitle, prospect.sourceSnippet,
        prospect.aiSummary, prospect.searchEventType, prospect.eventType,
      ].filter(Boolean).join(" ").toLowerCase();
      const itemEventType = prospect.searchEventType || prospect.eventType;
      return (
        (!search || searchable.includes(search)) &&
        (eventTypeFilter === "all" || itemEventType === eventTypeFilter) &&
        (scoreFilter === "all" || Number(prospect.fitScore) === Number(scoreFilter))
      );
    });
  };

  const todayApiRequests = Number(todayUsage.openAiRequests || 0) + Number(todayUsage.serperRequests || 0);

  const metrics = [
    ["review", "Discovery Review Queue", workflowCounts.review],
    ["email", "Email Outreach Queue", workflowCounts.email],
    ["social", "Social Outreach Queue", workflowCounts.social],
    ["research", "Research Queue", workflowCounts.research],
    ["followup", "Follow Up Queue", workflowCounts.followup],
    ["discovery", "Prospect Search List", workflowCounts.discovery],
    ["vendors", "Vendor URL List", workflowCounts.vendors],
    ["usage", "API Requests Today", todayApiRequests],
  ];

  const searchPhraseOptions = Array.from(
    new Set([
      ...searchPhrases.map((item) => item.searchPhrase),
    ].filter(Boolean))
  ).sort();

  const eventTypeOptions = Array.from(
    new Set([
      ...eventTypes,
      ...searchPhrases.map((item) => item.eventType),
    ].filter(Boolean))
  ).sort();

  const panelHasLoaded = (panel) => Boolean(panelPagination[panel]) || loadingPanel === panel;
  const panelVisibilityClass = (panel) => openPanels[panel] ? "" : "d-none";
  const filteredReviewCandidates = applyFilters((panelData.review || []).map((candidate) => ({
    ...candidate,
    eventType: candidate.searchEventType ||
      searchPhrases.find((phrase) => phrase.id === candidate.searchPhraseId)?.eventType ||
      candidate.eventType,
  })));
  const filteredVendors = (panelData.vendors || []).filter((vendor) => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return (
      (!normalizedSearch || [vendor.url, vendor.eventType, vendor.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)) &&
      (eventTypeFilter === "all" || vendor.eventType === eventTypeFilter)
    );
  });

  return (
    <div className="container mt-4 mb-5">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h2>Prospect Engine</h2>
          <p className="text-muted mb-0">
            Create prospects, review automated research and drafts, send outreach, and manage follow-up.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAddProspect((value) => !value)}>
            {showAddProspect ? "Hide Add a Prospect" : "Add a Prospect"}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleRunProspectWorkflow}
            disabled={Boolean(runningAutomation)}
          >
            {runningAutomation === "workflow" ? "Running Prospect Automation..." : "Run Research & Drafts"}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleRunSavedSearches}
            disabled={Boolean(runningAutomation)}
          >
            {runningAutomation === "discovery" ? "Running Saved Searches..." : "Run Saved Searches"}
          </button>
        </div>
      </div>

      <LGVNotificationBanner
        show={Boolean(status)}
        type={status?.type}
        message={status?.startedAt
          ? `${status.message} Elapsed: ${elapsedSeconds}s`
          : status?.message}
        onClose={status?.persistent ? null : () => setStatus(null)}
      />
      {showAddProspect && (
        <AddProspectForm
          formData={formData}
          setFormData={setFormData}
          eventTypes={eventTypeOptions}
          onSubmit={handleAddProspect}
          onCancel={() => setShowAddProspect(false)}
        />
      )}

      <div className="row mb-4">
        {metrics.map(([panel, label, value]) => (
          <div className="col-lg col-md-4 col-sm-6 mb-2" key={panel}>
            <button
              type="button"
              className={`card text-center w-100 h-100 ${openPanels[panel] ? "border-primary" : ""}`}
              onClick={() => handleMetricClick(panel)}
            >
              <div className="card-body"><h3>{value}</h3><div className="small text-muted">{label}</div></div>
            </button>
          </div>
        ))}
      </div>

      <WorkflowPanel id="filters" title="Search & Filters" open={openPanels.filters} onToggle={() => togglePanel("filters")}>
        <p className="text-muted">
          These filters apply to every loaded prospect queue and discovery result. They remain active as you switch between panels and pages.
        </p>
        <div className="row">
          <FilterField label="Search">
            <input className="form-control" value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search event, promoter, contact, location, or research..." />
          </FilterField>
          <FilterField label="Event Types">
            <select className="form-select" value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
              <option value="all">All Event Types</option>
              {eventTypeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </FilterField>
          <FilterField label="Score">
            <select className="form-select" value={scoreFilter} onChange={(event) => setScoreFilter(event.target.value)}>
              <option value="all">All Scores</option>
              {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((score) => <option key={score} value={score}>{score}/10</option>)}
            </select>
          </FilterField>
        </div>
      </WorkflowPanel>

      <WorkflowPanel id="prospectSearch" title="Prospect Search" open={openPanels.prospectSearch} onToggle={() => togglePanel("prospectSearch")}>
        <form onSubmit={handleProspectSearch}>
          <div className="row align-items-end">
            <div className="col-md-6 mb-3">
              <label className="form-label">Search Phrase</label>
              <input
                className="form-control"
                list="prospect-search-phrases"
                value={prospectSearch.searchPhrase}
                onChange={(event) => handleProspectSearchChange("searchPhrase", event.target.value)}
                required
              />
              <datalist id="prospect-search-phrases">
                {searchPhraseOptions.map((phrase) => <option key={phrase} value={phrase} />)}
              </datalist>
              <div className="form-text">Enter a Google search phrase or a specific event-listing URL.</div>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Saved Event Type</label>
              <select
                className="form-select"
                value={prospectSearch.eventType}
                onChange={(event) => setProspectSearch((current) => ({
                  ...current,
                  eventType: event.target.value,
                  newEventType: "",
                }))}
              >
                <option value="">Select an Event Type</option>
                {eventTypes.map((eventType) => (
                  <option key={eventType} value={eventType}>{eventType}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Add New Event Type</label>
              <input
                className="form-control"
                value={prospectSearch.newEventType}
                onChange={(event) => setProspectSearch((current) => ({
                  ...current,
                  eventType: "",
                  newEventType: event.target.value,
                }))}
              />
            </div>
            <div className="col-md-8 mb-3">
              <div className="form-check">
                <input
                  id="prospect-search-one-time"
                  type="checkbox"
                  className="form-check-input"
                  checked={prospectSearch.oneTime}
                  disabled={prospectSearch.vendor}
                  onChange={(event) => setProspectSearch((current) => ({
                    ...current,
                    oneTime: event.target.checked,
                  }))}
                />
                <label className="form-check-label" htmlFor="prospect-search-one-time">
                  One-time search. Do not save this phrase for weekly searches.
                </label>
              </div>
              <div className="form-check mt-2">
                <input
                  id="prospect-search-vendor"
                  type="checkbox"
                  className="form-check-input"
                  checked={prospectSearch.vendor}
                  onChange={(event) => setProspectSearch((current) => ({
                    ...current,
                    vendor: event.target.checked,
                    oneTime: event.target.checked ? false : current.oneTime,
                  }))}
                />
                <label className="form-check-label" htmlFor="prospect-search-vendor">
                  Save this URL as a Vendor to contact later. Do not search it for events.
                </label>
              </div>
            </div>
            <div className="col-md-4 mb-3 text-md-end">
              <button type="submit" className="btn btn-primary w-100" disabled={searching}>
                {searching
                  ? prospectSearch.vendor ? "Saving Vendor..." : "Searching..."
                  : prospectSearch.vendor
                    ? "Save Vendor"
                    : prospectSearch.oneTime
                    ? "Search Once"
                    : "Save & Search"}
              </button>
            </div>
          </div>
        </form>
      </WorkflowPanel>

      {loading ? <div className="alert alert-info">Loading Prospect Engine...</div> : (
        <>
          {panelHasLoaded("review") && <div className={panelVisibilityClass("review")}>
          <CandidateReviewPanel
            candidates={filteredReviewCandidates}
            discoveryJobs={panelData.discovery || []}
            searchPhrases={searchPhrases}
            totalCount={workflowCounts.review}
            pagination={panelPagination.review}
            loading={loadingPanel === "review"}
            onPrevious={() => previousPanelPage("review")}
            onNext={() => nextPanelPage("review")}
            open
            onToggle={() => togglePanel("review")}
            onReview={handleReviewCandidate}
            onCollect={handleCollectCandidate}
            working={working}
          />
          </div>}
          {panelHasLoaded("email") && <div className={panelVisibilityClass("email")}>
          <ProspectQueuePanel
            id="email" title="Email Outreach Queue" description="Drafted prospects with an email address that have not been contacted."
            emptyMessage="No email outreach is waiting to be sent." open onToggle={() => togglePanel("email")}
            prospects={applyFilters(panelData.email)} totalCount={workflowCounts.email} draftEdits={draftEdits} onDraftChange={changeDraft}
            actionLabel="Send Mail" onAction={handleSendEmail} working={working}
            secondaryActionLabel="Recreate Draft" onSecondaryAction={handleRecreateDraft}
            onArchive={(prospect) => handleArchiveProspect(prospect, "email")}
            pagination={panelPagination.email} loading={loadingPanel === "email"}
            onPrevious={() => previousPanelPage("email")} onNext={() => nextPanelPage("email")}
          />
          </div>}
          {panelHasLoaded("social") && <div className={panelVisibilityClass("social")}>
          <ProspectQueuePanel
            id="social" title="Social Outreach Queue" description="Drafted prospects without an email address. Send the message manually on the listed social channel."
            emptyMessage="No social outreach is waiting to be sent." open onToggle={() => togglePanel("social")}
            prospects={applyFilters(panelData.social)} totalCount={workflowCounts.social} draftEdits={draftEdits} onDraftChange={changeDraft}
            actionLabel="Message Sent" onAction={handleSocialSent} working={working}
            secondaryActionLabel="Recreate Draft" onSecondaryAction={handleRecreateDraft}
            onCopyDraft={handleCopyDraft}
            onArchive={(prospect) => handleArchiveProspect(prospect, "social")}
            pagination={panelPagination.social} loading={loadingPanel === "social"}
            onPrevious={() => previousPanelPage("social")} onNext={() => nextPanelPage("social")}
          />
          </div>}
          {panelHasLoaded("research") && <div className={panelVisibilityClass("research")}>
          <ProspectQueuePanel
            id="research" title="Research Queue" description="New prospects waiting for event research, social research, and an outreach draft."
            emptyMessage="No prospects are waiting for research." open onToggle={() => togglePanel("research")}
            prospects={applyFilters(panelData.research)} totalCount={workflowCounts.research} actionLabel="Research" onAction={handleResearch} working={working} hideDraft
            onArchive={(prospect) => handleArchiveProspect(prospect, "research")}
            pagination={panelPagination.research} loading={loadingPanel === "research"}
            onPrevious={() => previousPanelPage("research")} onNext={() => nextPanelPage("research")}
          />
          </div>}
          {panelHasLoaded("followup") && <div className={panelVisibilityClass("followup")}>
          <ProspectQueuePanel
            id="followup" title="Follow Up Queue" description="Prospects that have already received email or social outreach."
            emptyMessage="No prospects are waiting for follow-up." open onToggle={() => togglePanel("followup")}
            prospects={applyFilters(panelData.followup)} totalCount={workflowCounts.followup} actionLabel="Archive" onAction={(prospect) => handleArchiveProspect(prospect, "followup")} working={working} showSentDate
            pagination={panelPagination.followup} loading={loadingPanel === "followup"}
            onPrevious={() => previousPanelPage("followup")} onNext={() => nextPanelPage("followup")}
          />
          </div>}
          {panelHasLoaded("discovery") && <div className={panelVisibilityClass("discovery")}>
          <WorkflowPanel id="discovery" title={`Prospect Search List (${workflowCounts.discovery})`} open onToggle={() => togglePanel("discovery")}>
            {loadingPanel === "discovery" && (panelData.discovery || []).length === 0 ? <PanelLoading /> : <>
            <DiscoveryJobs
              jobs={panelData.discovery || []}
              phrases={searchPhrases}
              onDelete={handleDeleteDiscovery}
              onRerun={handleRerunDiscovery}
              working={working}
              searchText={searchText}
              eventTypeFilter={eventTypeFilter}
            />
            <PanelPagination pagination={panelPagination.discovery} loading={loadingPanel === "discovery"} onPrevious={() => previousPanelPage("discovery")} onNext={() => nextPanelPage("discovery")} />
            </>}
          </WorkflowPanel>
          </div>}
          {panelHasLoaded("vendors") && <div className={panelVisibilityClass("vendors")}>
          <VendorUrlPanel
            vendors={filteredVendors}
            totalCount={workflowCounts.vendors}
            open
            onToggle={() => togglePanel("vendors")}
            pagination={panelPagination.vendors}
            loading={loadingPanel === "vendors"}
            onPrevious={() => previousPanelPage("vendors")}
            onNext={() => nextPanelPage("vendors")}
          />
          </div>}
          {panelHasLoaded("usage") && <div className={panelVisibilityClass("usage")}>
          <ApiUsagePanel
            usage={panelData.usage || []} open onToggle={() => togglePanel("usage")}
            pagination={panelPagination.usage} loading={loadingPanel === "usage"}
            onPrevious={() => previousPanelPage("usage")} onNext={() => nextPanelPage("usage")}
          />
          </div>}
        </>
      )}
    </div>
  );
}

function WorkflowPanel({ id, title, open, onToggle, children }) {
  return (
    <div className="card mb-3">
      <div className="card-header p-0">
        <button type="button" className="btn w-100 d-flex justify-content-between align-items-center text-start px-3 py-2" onClick={onToggle} aria-expanded={open} aria-controls={`${id}-panel`}>
          <strong>{title}</strong><span aria-hidden="true">{open ? "−" : "+"}</span>
        </button>
      </div>
      {open && <div className="card-body" id={`${id}-panel`}>{children}</div>}
    </div>
  );
}

function CandidateReviewPanel({
  candidates,
  discoveryJobs,
  searchPhrases,
  totalCount,
  pagination,
  loading,
  onPrevious,
  onNext,
  open,
  onToggle,
  onReview,
  onCollect,
  working,
}) {
  return (
    <WorkflowPanel
      id="review"
      title={`Discovery Review Queue (${totalCount})`}
      open={open}
      onToggle={onToggle}
    >
      <p className="text-muted">
        Results that need a decision are retained here. Likely events move directly to Research. Approve a remaining event to research it and write its draft, or archive it for duplicate prevention.
      </p>
      {loading && candidates.length === 0 ? <PanelLoading /> : candidates.length === 0 ? (
        <div className="alert alert-info mb-0">No discovery results are waiting for review.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead><tr>
              <th style={{ minWidth: "340px" }}>Discovered Event</th>
              <th style={{ minWidth: "320px" }}>Discovery Assessment</th>
              <th>Action</th>
            </tr></thead>
            <tbody>{candidates.map((candidate) => {
              const busy = Boolean(working[candidate.id]);
              const selectedEventType = candidate.searchEventType ||
                searchPhrases.find((phrase) => phrase.id === candidate.searchPhraseId)?.eventType ||
                discoveryJobs.find((job) => job.id === candidate.discoveryJobId)?.eventType ||
                candidate.eventType ||
                "Unknown";
              return <tr key={candidate.id}>
                <td>
                  <strong>{candidate.eventName || candidate.sourceTitle || "Unnamed result"}</strong>
                  <div className="small mt-1"><strong>Event Type:</strong> {selectedEventType}</div>
                  <div className={`small ${isPastEvent(candidate) ? "text-muted" : ""}`}>
                    <strong>Event Date:</strong> {displayProspectDate(candidate)}
                    {isPastEvent(candidate) && " (past)"}
                  </div>
                  <div className="small"><strong>Score:</strong> {Number(candidate.fitScore || 0)}/10</div>
                  <div className="small"><strong>Search Page:</strong> {candidate.searchPage || 1}</div>
                  {candidate.sourceUrl && <div className="small mt-1">
                    <a href={candidate.sourceUrl} target="_blank" rel="noreferrer">Open source result</a>
                  </div>}
                </td>
                <td>
                  <span className={`badge ${candidate.isLikelyEvent ? "bg-success" : "bg-warning text-dark"}`}>
                    {candidate.isLikelyEvent ? "Likely event" : "Needs human review"}
                  </span>
                  <div className="small mt-2">{candidate.aiSummary || candidate.sourceSnippet || "No assessment available."}</div>
                  {candidate.agentReasoning && <div className="small mt-2"><strong>Reasoning:</strong> {candidate.agentReasoning}</div>}
                  {candidate.aiEnrichmentStatus === "unavailable" && <div className="small text-muted mt-2">AI enrichment was unavailable; the original result was still retained.</div>}
                </td>
                <td>
                  <div className="d-flex flex-column gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => onReview(candidate, "approve")}
                      disabled={busy}
                    >
                      {working[candidate.id] === "approve" ? "Researching..." : "Research"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => onCollect(candidate)}
                      disabled={busy || !candidate.sourceUrl}
                      title={candidate.sourceUrl ? "Run Prospect Search against this source URL" : "No source URL is available"}
                    >
                      {working[candidate.id] === "collection" ? "Collecting..." : "Collection"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => onReview(candidate, "archive")}
                      disabled={busy}
                    >
                      {working[candidate.id] === "archive" ? "Archiving..." : "Archive"}
                    </button>
                  </div>
                </td>
              </tr>;
            })}</tbody>
          </table>
        </div>
      )}
      <PanelPagination pagination={pagination} loading={loading} onPrevious={onPrevious} onNext={onNext} />
    </WorkflowPanel>
  );
}

function ApiUsagePanel({ usage, open, onToggle, pagination, loading, onPrevious, onNext }) {
  const totals = usage.reduce((summary, item) => ({
    openAiRequests: summary.openAiRequests + Number(item.openAiRequests || 0),
    inputTokens: summary.inputTokens + Number(item.inputTokens || 0),
    outputTokens: summary.outputTokens + Number(item.outputTokens || 0),
    totalTokens: summary.totalTokens + Number(item.totalTokens || 0),
    serperRequests: summary.serperRequests + Number(item.serperRequests || 0),
  }), { openAiRequests: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0, serperRequests: 0 });

  return (
    <WorkflowPanel id="usage" title="API Usage (25 Most Recent Days)" open={open} onToggle={onToggle}>
      <div className="row mb-3">
        <div className="col-md"><strong>OpenAI Requests</strong><div>{totals.openAiRequests.toLocaleString()}</div></div>
        <div className="col-md"><strong>Input Tokens</strong><div>{totals.inputTokens.toLocaleString()}</div></div>
        <div className="col-md"><strong>Output Tokens</strong><div>{totals.outputTokens.toLocaleString()}</div></div>
        <div className="col-md"><strong>Total Tokens</strong><div>{totals.totalTokens.toLocaleString()}</div></div>
        <div className="col-md"><strong>Serper Searches</strong><div>{totals.serperRequests.toLocaleString()}</div></div>
      </div>
      {loading && usage.length === 0 ? <PanelLoading /> : usage.length === 0 ? (
        <div className="alert alert-info mb-0">No tracked external API usage yet. Local test runs record zero external usage.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead><tr><th>Date</th><th>OpenAI Requests</th><th>Input Tokens</th><th>Output Tokens</th><th>Total Tokens</th><th>Serper Searches</th></tr></thead>
            <tbody>{usage.map((item) => <tr key={item.id}>
              <td>{item.date || item.id}</td>
              <td>{Number(item.openAiRequests || 0).toLocaleString()}</td>
              <td>{Number(item.inputTokens || 0).toLocaleString()}</td>
              <td>{Number(item.outputTokens || 0).toLocaleString()}</td>
              <td>{Number(item.totalTokens || 0).toLocaleString()}</td>
              <td>{Number(item.serperRequests || 0).toLocaleString()}</td>
            </tr>)}</tbody>
          </table>
        </div>
      )}
      <PanelPagination pagination={pagination} loading={loading} onPrevious={onPrevious} onNext={onNext} />
    </WorkflowPanel>
  );
}

function VendorUrlPanel({ vendors, totalCount, open, onToggle, pagination, loading, onPrevious, onNext }) {
  return (
    <WorkflowPanel id="vendors" title={`Vendor URL List (${totalCount})`} open={open} onToggle={onToggle}>
      <p className="text-muted">URLs identified as potential vendors to contact later.</p>
      {loading && vendors.length === 0 ? <PanelLoading /> : vendors.length === 0 ? (
        <div className="alert alert-info mb-0">No Vendor URLs match the current filters.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead><tr><th>Vendor URL</th><th>Event Type</th><th>Status</th><th>Date Added</th></tr></thead>
            <tbody>{vendors.map((vendor) => <tr key={vendor.id}>
              <td><a href={vendor.url} target="_blank" rel="noreferrer">{vendor.url}</a></td>
              <td>{vendor.eventType || "Not listed"}</td>
              <td>To contact</td>
              <td>{vendor.createdAt?.toDate ? vendor.createdAt.toDate().toLocaleString() : "Not recorded"}</td>
            </tr>)}</tbody>
          </table>
        </div>
      )}
      <PanelPagination pagination={pagination} loading={loading} onPrevious={onPrevious} onNext={onNext} />
    </WorkflowPanel>
  );
}

function ProspectQueuePanel({
  id, title, description, emptyMessage, open, onToggle, prospects, draftEdits,
  onDraftChange, actionLabel, onAction, working, hideDraft = false, showSentDate = false,
  secondaryActionLabel = "", onSecondaryAction, totalCount, pagination, loading,
  onArchive, onCopyDraft, onPrevious, onNext,
}) {
  return (
    <WorkflowPanel id={id} title={`${title} (${totalCount})`} open={open} onToggle={onToggle}>
      <p className="text-muted">{description}</p>
      {loading && prospects.length === 0 ? <PanelLoading /> : prospects.length === 0 ? <div className="alert alert-info mb-0">{emptyMessage}</div> : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle mb-0">
            <thead><tr>
              <th style={{ minWidth: "320px" }}>Prospect Details</th>
              {!hideDraft && <th style={{ minWidth: "360px" }}>Draft Message</th>}
              {showSentDate && <th>Date Mailed</th>}
              <th>Action</th>
            </tr></thead>
            <tbody>
              {prospects.map((prospect) => {
                const draft = draftEdits?.[prospect.id] || { subject: prospect.outreachSubject || "", body: draftBody(prospect) };
                const busy = Boolean(working[prospect.id]);
                return (
                  <tr key={prospect.id}>
                    <td><ProspectDetails prospect={prospect} /></td>
                    {!hideDraft && <td>
                      {onDraftChange ? <>
                        <label className="form-label small mb-1">Subject</label>
                        <input className="form-control form-control-sm mb-2" value={draft.subject} onChange={(event) => onDraftChange(prospect, "subject", event.target.value)} />
                        <label className="form-label small mb-1">Message</label>
                        <textarea className="form-control form-control-sm" rows="9" value={draft.body} onChange={(event) => onDraftChange(prospect, "body", event.target.value)} />
                        {onCopyDraft && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={() => onCopyDraft(prospect)}
                          >
                            Copy
                          </button>
                        )}
                      </> : <div style={{ whiteSpace: "pre-wrap" }}>{draft.body || "No draft"}</div>}
                    </td>}
                    {showSentDate && <td>{prospect.lastOutreachDate || prospect.lastContacted || "Not recorded"}</td>}
                    <td>
                      <div className="d-flex flex-column gap-2">
                        <button type="button" className={`btn btn-sm ${actionLabel === "Archive" ? "btn-outline-secondary" : "btn-outline-primary"}`} onClick={() => onAction(prospect)} disabled={busy}>
                          {working[prospect.id] === "send" ? "Sending..." : working[prospect.id] === "research" ? "Researching..." : working[prospect.id] === "social" ? "Saving..." : working[prospect.id] === "archive" ? "Archiving..." : actionLabel}
                        </button>
                        {secondaryActionLabel && onSecondaryAction && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => onSecondaryAction(prospect)}
                            disabled={busy}
                          >
                            {working[prospect.id] === "draft" ? "Recreating..." : secondaryActionLabel}
                          </button>
                        )}
                        {onArchive && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => onArchive(prospect)}
                            disabled={busy}
                          >
                            {working[prospect.id] === "archive" ? "Archiving..." : "Archive"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PanelPagination pagination={pagination} loading={loading} onPrevious={onPrevious} onNext={onNext} />
    </WorkflowPanel>
  );
}

function PanelLoading() {
  return <div className="alert alert-info mb-0">Loading the most recent {DASHBOARD_PAGE_SIZE} records...</div>;
}

function PanelPagination({ pagination = {}, loading, onPrevious, onNext }) {
  const pageIndex = Number(pagination.pageIndex || 0);
  if (pageIndex === 0 && !pagination.hasMore) return null;
  return (
    <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onPrevious} disabled={loading || pageIndex === 0}>
        Previous
      </button>
      <span className="small text-muted">Page {pageIndex + 1}</span>
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={onNext} disabled={loading || !pagination.hasMore}>
        {loading ? "Loading..." : "Next 25"}
      </button>
    </div>
  );
}

function ProspectDetails({ prospect }) {
  const socials = [
    ["Facebook", prospect.facebookUrl], ["Instagram", prospect.instagramUrl],
    ["LinkedIn", prospect.linkedInUrl], ["YouTube", prospect.youtubeUrl], ["TikTok", prospect.tiktokUrl],
  ].filter(([, url]) => Boolean(url));

  return <div>
    <strong>{prospect.eventName || "Unnamed prospect"}</strong>
    <div className="small mt-1"><strong>Promoter / Contact / Website:</strong>{" "}
      {[prospect.organizationName, prospect.contactName].filter(Boolean).join(" / ") || "Not listed"}
      {prospect.email && <div>{prospect.email}</div>}
      {prospect.website && <div><a href={prospect.website} target="_blank" rel="noreferrer">{prospect.website}</a></div>}
    </div>
    {prospect.researchSummary && <div className="small mt-2"><strong>Event Research:</strong> {prospect.researchSummary}</div>}
    <div className="small mt-2"><strong>Socials:</strong>{" "}
      {socials.length ? socials.map(([label, url], index) => <React.Fragment key={label}>{index > 0 && " · "}<a href={url} target="_blank" rel="noreferrer">{label}</a></React.Fragment>) : "Not listed"}
    </div>
    {prospect.socialResearchSummary && <div className="small mt-2"><strong>Social Research:</strong> {prospect.socialResearchSummary}</div>}
    <div className="small mt-2"><strong>Event Type:</strong> {prospect.eventType || "Unknown"}</div>
    <div className={`small ${isPastEvent(prospect) ? "text-muted" : ""}`}><strong>Event Date:</strong> {displayProspectDate(prospect)}{isPastEvent(prospect) && " (past)"}</div>
    <div className="small"><strong>Score:</strong> {Number(prospect.fitScore || 0)}/10</div>
  </div>;
}

function AddProspectForm({ formData, setFormData, eventTypes, onSubmit, onCancel }) {
  const change = (field, value) => setFormData((current) => ({ ...current, [field]: value }));
  return <div className="card mb-4">
    <div className="card-header"><strong>Add a Prospect</strong></div>
    <div className="card-body"><form onSubmit={onSubmit}><div className="row">
      <FormField width="col-md-6" label="Event Name *"><input className="form-control" required value={formData.eventName} onChange={(event) => change("eventName", event.target.value)} /></FormField>
      <FormField width="col-md-3" label="Event Type"><select className="form-select" value={formData.eventType} onChange={(event) => change("eventType", event.target.value)}>{eventTypes.map((type) => <option key={type}>{type}</option>)}</select></FormField>
      <FormField width="col-md-3" label="Event Date"><input type="date" className="form-control" value={formData.eventDate} onChange={(event) => change("eventDate", event.target.value)} /></FormField>
      <FormField width="col-md-6" label="Organization / Promoter"><input className="form-control" value={formData.organizationName} onChange={(event) => change("organizationName", event.target.value)} /></FormField>
      <FormField width="col-md-3" label="Contact Name"><input className="form-control" value={formData.contactName} onChange={(event) => change("contactName", event.target.value)} /></FormField>
      <FormField width="col-md-3" label="Email"><input type="email" className="form-control" value={formData.email} onChange={(event) => change("email", event.target.value)} /></FormField>
      <FormField width="col-md-4" label="Website"><input type="url" className="form-control" value={formData.website} onChange={(event) => change("website", event.target.value)} /></FormField>
      <FormField width="col-md-4" label="Facebook URL"><input type="url" className="form-control" value={formData.facebookUrl} onChange={(event) => change("facebookUrl", event.target.value)} /></FormField>
      <FormField width="col-md-4" label="Instagram URL"><input type="url" className="form-control" value={formData.instagramUrl} onChange={(event) => change("instagramUrl", event.target.value)} /></FormField>
      <FormField width="col-md-3" label="City"><input className="form-control" value={formData.city} onChange={(event) => change("city", event.target.value)} /></FormField>
      <FormField width="col-md-2" label="State"><input className="form-control" maxLength="2" value={formData.state} onChange={(event) => change("state", event.target.value)} /></FormField>
      <FormField width="col-md-3" label={`Score: ${formData.fitScore}/10`}><input type="range" className="form-range" min="1" max="10" value={formData.fitScore} onChange={(event) => change("fitScore", event.target.value)} /></FormField>
      <FormField width="col-md-4" label="Notes"><textarea className="form-control" rows="2" value={formData.notes} onChange={(event) => change("notes", event.target.value)} /></FormField>
    </div>
      <button type="submit" className="btn btn-primary me-2">Add Prospect</button>
      <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Cancel</button>
    </form></div>
  </div>;
}

function FormField({ width, label, children }) {
  return <div className={`${width} mb-3`}><label className="form-label">{label}</label>{children}</div>;
}

function FilterField({ label, children }) {
  return <div className="col-md-4 mb-3"><label className="form-label">{label}</label>{children}</div>;
}

function DiscoveryJobs({ jobs, phrases, onDelete, onRerun, working, searchText, eventTypeFilter }) {
  const normalizedSearch = String(searchText || "").trim().toLowerCase();
  const latestByPhrase = jobs
  .filter((job) => !job.phraseId || phrases.some((item) => item.id === job.phraseId))
  .map((job) => {
    const phrase = phrases.find((item) => item.id === job.phraseId);
    return {
      ...(phrase || {}),
      id: phrase?.id || `job-${job.id}`,
      rowKey: job.id,
      searchPhrase: phrase?.searchPhrase || job.searchTerm,
      eventType: phrase?.eventType || job.eventType,
      oneTime: !phrase,
      latestJob: job,
    };
  }).filter((item) => {
    const matchesSearch = !normalizedSearch || [item.searchPhrase, item.eventType]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(normalizedSearch);
    const matchesEventType = eventTypeFilter === "all" || item.eventType === eventTypeFilter;
    return matchesSearch && matchesEventType;
  });

  if (!latestByPhrase.length) return <div className="alert alert-info mb-0">No prospect searches have been run.</div>;
  return <div className="table-responsive"><table className="table table-sm table-bordered align-middle mb-0">
    <thead><tr><th>Search Phrase</th><th>Event Type</th><th>Page</th><th>Last Search</th><th>Results</th><th>Error</th><th>Actions</th></tr></thead>
    <tbody>{latestByPhrase.map((phrase) => {
      const job = phrase.latestJob || {};
      const searchedAt = phrase.lastSearchedAt || job.completedAt || job.startedAt;
      const rerunKey = `discovery-rerun-${phrase.id}`;
      const deleteKey = `discovery-delete-${phrase.id}`;
      const rowWorking = Boolean(working[rerunKey] || working[deleteKey]);
      const searchPhrase = String(phrase.searchPhrase || "").trim();
      const searchHref = /^https?:\/\//i.test(searchPhrase)
        ? searchPhrase
        : `https://www.google.com/search?q=${encodeURIComponent(searchPhrase)}`;
      const totalResultsFound = phrase.totalResultsFound ?? job.resultsFound ?? 0;
      return <tr key={phrase.rowKey}>
      <td>
        <a href={searchHref} target="_blank" rel="noreferrer">{searchPhrase}</a>
        {phrase.oneTime && <div><span className="badge bg-info text-dark">One-time</span></div>}
      </td>
      <td>{phrase.eventType || job.eventType || "All"}</td>
      <td>
        {job.searchMode === "url_scan"
          ? <span className="small text-muted">Full-page rescan</span>
          : <>{phrase.lastSearchPage || job.searchPage || "–"} / 5<br /><span className="small text-muted">{phrase.initialCrawlCompleted ? "Weekly page 1 refresh" : `Next: ${phrase.nextSearchPage || 1}`}</span></>}
      </td>
      <td>{searchedAt?.toDate ? searchedAt.toDate().toLocaleString() : "Not run"}</td>
      <td>
        Found: {phrase.lastResultsFound ?? job.resultsFound ?? 0} · Moved to Research: {phrase.lastProspectsCreated ?? job.prospectsCreated ?? 0} · Saved for review: {phrase.lastCandidatesCreated ?? job.candidatesCreated ?? 0}
        {` · Duplicates: ${phrase.lastDuplicatesSkipped ?? job.duplicatesSkipped ?? 0} · Total: ${totalResultsFound}`}
      </td>
      <td>{phrase.lastSearchError || job.errorMessage || ""}</td>
      <td>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => onRerun(phrase)}
            disabled={rowWorking}
          >
            {working[rerunKey] ? "Rerunning..." : "Rerun"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onDelete(phrase)}
            disabled={rowWorking}
          >
            {working[deleteKey] ? "Deleting..." : "Delete"}
          </button>
        </div>
      </td>
    </tr>})}</tbody>
  </table></div>;
}

export default SalesDashboardMerged;
